/**
 * High Resolution QR Code Utilities
 *
 * Generates print/large-display quality QR codes matching the brand specification:
 * - Error correction: High (Level H) for scan reliability with center icon
 * - QR Module Color: #F29F05 (RGB: 242, 159, 5)
 * - Background: #FFFFFF (White)
 * - Quiet Zone Margin: 4 modules
 * - Center Icon: 1/4 (25%) of QR dimension with clean white badge padding
 * - Resolution: 2048x2048 px native raster export, plus Vector SVG export
 */

export const QR_BRAND_COLOR = "#F29F05";
export const QR_BG_COLOR = "#FFFFFF";
export const DEFAULT_HIGH_RES_SIZE = 4096;
export const DEFAULT_PREVIEW_SIZE = 240;

// Original TrackYourImpact logo dimensions (2782 x 3241) to preserve exact aspect ratio
export const TYI_LOGO_WIDTH = 2782;
export const TYI_LOGO_HEIGHT = 3241;
export const TYI_LOGO_ASPECT_RATIO = TYI_LOGO_WIDTH / TYI_LOGO_HEIGHT; // ~0.858377

/**
 * Returns { width, height } for center logo maintaining exact original aspect ratio.
 */
export function getLogoDimensions(maxDimension: number) {
  const height = maxDimension;
  const width = Math.round(maxDimension * TYI_LOGO_ASPECT_RATIO);
  return { width, height };
}

export interface HighResQrConfig {
  value: string;
  size?: number;
  fgColor?: string;
  bgColor?: string;
  logoSrc?: string;
  level?: "L" | "M" | "Q" | "H";
  marginSize?: number;
}

/**
 * Triggers a file download in the browser from a data URL or Blob URL.
 */
export function downloadFile(urlOrData: string, filename: string): void {
  const link = document.createElement("a");
  link.href = urlOrData;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Downloads a canvas element as a PNG file.
 */
export function downloadCanvasAsPng(canvas: HTMLCanvasElement, filename: string): void {
  if (canvas.toBlob) {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          const dataUrl = canvas.toDataURL("image/png");
          downloadFile(dataUrl, filename);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        downloadFile(objectUrl, filename);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      },
      "image/png",
      1.0
    );
  } else {
    const dataUrl = canvas.toDataURL("image/png");
    downloadFile(dataUrl, filename);
  }
}

/**
 * Downloads an SVG element as an SVG vector file (infinite resolution).
 */
export function downloadSvgElement(svg: SVGSVGElement, filename: string): void {
  const serializer = new XMLSerializer();
  let source = serializer.serializeToString(svg);

  if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
    source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
  }
  if (!source.match(/^<svg[^>]+xmlns:xlink="http:\/\/www\.w3\.org\/1999\/xlink"/)) {
    source = source.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
  }

  const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);
  downloadFile(svgUrl, filename.endsWith(".svg") ? filename : `${filename}.svg`);
  setTimeout(() => URL.revokeObjectURL(svgUrl), 10000);
}
